import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Send } from "lucide-react";
import { toast } from "sonner";
import { isReadOnlyGuest } from "@/lib/roles";

export default function ChatPage() {
  const { user } = useAuth();
  const readOnlyGuest = isReadOnlyGuest(user);
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => { api.get("/chat/contacts").then(r => setContacts(r.data)); }, []);
  useEffect(() => {
    if (!active) return;
    const load = () => api.get(`/chat/messages/${active.id}`).then(r => setMessages(r.data));
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      await api.post("/chat/messages", { to_user_id: active.id, text });
      setText("");
      const { data } = await api.get(`/chat/messages/${active.id}`); setMessages(data);
    } catch { toast.error("Send failed"); }
  };

  const downloadTranscript = () => {
    if (!active) return;
    const txt = messages.map(m => `[${m.created_at}] ${m.from_name}: ${m.text}`).join("\n");
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `chat-${active.name.replace(/\s+/g, "_")}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="mb-6 flex justify-between items-end">
        <div>
          <p className="label-eyebrow">Internal messaging</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Chat</h1>
          <p className="text-gray-500 mt-1 text-sm">Web chat between Admins, Distributing Partners and Warehouse. SMS bridge MOCKED — install Twilio integration to enable.</p>
          {readOnlyGuest && <p className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="guest-chat-read-only-notice">Guest access is read-only.</p>}
        </div>
        {active && <Button variant="outline" onClick={downloadTranscript} data-testid="download-transcript"><Download size={14} className="mr-1" /> Download transcript</Button>}
      </header>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="p-3 lg:col-span-1">
          <p className="label-eyebrow mb-2 px-2">Contacts</p>
          <ul className="space-y-1">
            {contacts.map(c => (
              <li key={c.id}>
                <button onClick={() => setActive(c)}
                        className={`w-full text-left px-3 py-2 rounded text-sm ${active?.id === c.id ? "bg-[var(--ink)] text-white" : "hover:bg-[var(--bg-soft)]"}`}
                        data-testid={`chat-contact-${c.id}`}>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs opacity-70 capitalize">{c.role}</div>
                </button>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="lg:col-span-3 p-4 flex flex-col" style={{ minHeight: 500 }}>
          {!active ? <div className="text-center text-gray-400 my-auto">Select a contact to start.</div> :
            <>
              <div className="flex-1 overflow-y-auto space-y-2 mb-3" data-testid="chat-pane">
                {messages.map(m => (
                  <div key={m.id} className={`max-w-md ${m.from_user_id === user.id ? "ml-auto" : ""}`}>
                    <div className={`px-3 py-2 rounded-lg text-sm ${m.from_user_id === user.id ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-soft)]"}`}>{m.text}</div>
                    <div className="text-[10px] text-gray-400 mt-1">{m.from_name} · {m.created_at?.slice(11, 16)}</div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              {readOnlyGuest ? <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">Messages are read-only.</p> : <div className="flex gap-2">
                <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Type a message…" data-testid="chat-input" />
                <Button className="btn-brand" onClick={send} data-testid="chat-send"><Send size={14} /></Button>
              </div>}
            </>
          }
        </Card>
      </div>
    </div>
  );
}
